import type { UserCategory } from "@nlc/shared-types";

import { USER_CATEGORIES, USER_ROLES } from "@nlc/shared-types";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import type { UserRole } from "@/features/auth/permissions/config";

import { UsersKickstarterBanner } from "@/components/kickstarter-banner/banners";
import { UnlockModal } from "@/components/upgrade-modals/unlock-modal";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess";
import { FeaturePageLayout } from "@/features/shared/layout";
import { usePaginationWithDynamicPageSize } from "@/hooks/usePaginationWithDynamicPageSize";
import { useUser } from "@/hooks/useUser";
import { getPageSizeOptions } from "@/utils/getPageSizeOptions";
import { isKickstarterAccount } from "@/utils/isKickstarterAccount";

import { UsersTable } from "./components/UsersTable";
import { useGetUsers } from "./shared/hooks/useGetUsers";

export function UsersPage() {
  const [category, setCategory] = useState<UserCategory>(
    USER_CATEGORIES.CUSTOMERS,
  );
  const [isUnlockModalOpen, setIsUnlockModalOpen] = useState(false);

  const { hasRole } = useRoleAccess();
  const { data: userData } = useUser();
  const navigate = useNavigate();

  // Initialize pagination hook
  const {
    debouncedQuery,
    inputValue,
    itemsPerPage,
    page,
    setInputValue,
    setItemsPerPage,
    setPage,
    setSortDescriptor,
    sortDescriptor,
    updateMetadata,
  } = usePaginationWithDynamicPageSize();

  const { data: response, isFetching } = useGetUsers({
    category,
    direction: sortDescriptor.direction === "descending" ? "desc" : "asc",
    itemsPerPage,
    page,
    search: debouncedQuery,
    sortBy: sortDescriptor?.column as string,
  });

  const users = response?.users || [];
  const metadata = response?.metadata;

  // Update metadata when response changes
  useEffect(() => {
    updateMetadata(metadata);
  }, [metadata, updateMetadata]);

  const getAvailableTabs = () => {
    if (hasRole([USER_ROLES.SUPER_ADMIN, USER_ROLES.CUSTOMER_SUCCESS])) {
      return [
        { id: USER_CATEGORIES.CUSTOMERS, label: "Customers" },
        { id: USER_CATEGORIES.DESIGNERS, label: "Designers" },
        { id: USER_CATEGORIES.TEAM_LEADERS, label: "Team Leaders" },
        { id: USER_CATEGORIES.CUSTOMER_SUCCESS, label: "Customer Success" },
        { id: USER_CATEGORIES.SUPER_ADMINS, label: "Super Admins" },
        { id: USER_CATEGORIES.ARCHIVED, label: "Archived" },
        { id: USER_CATEGORIES.ALL, label: "All" },
      ];
    } else if (hasRole([USER_ROLES.TEAM_LEADER])) {
      return [
        { id: USER_CATEGORIES.CUSTOMERS, label: "Customers" },
        { id: USER_CATEGORIES.DESIGNERS, label: "Designers" },
        {
          id: USER_CATEGORIES.ASSISTANT_TEAM_LEADERS,
          label: "Assistant Team Leaders",
        },
        { id: USER_CATEGORIES.ARCHIVED, label: "Archived" },
      ];
    } else if (
      hasRole([USER_ROLES.OWNER, USER_ROLES.ADMIN]) &&
      !hasRole([USER_ROLES.COLLABORATOR])
    ) {
      return [{ id: USER_CATEGORIES.CUSTOMERS, label: "Team Members" }];
    }

    return [];
  };

  const availableTabs = getAvailableTabs();
  const showTabs = availableTabs.length > 1;

  // Set default category based on available tabs
  useEffect(() => {
    if (
      availableTabs.length > 0 &&
      !availableTabs.some((tab) => tab.id === category)
    ) {
      const firstTab = availableTabs[0];

      if (firstTab) {
        setCategory(firstTab.id as UserCategory);
      }
    }
  }, [availableTabs, category]);

  // Collaborators should not access this page
  if (hasRole([USER_ROLES.COLLABORATOR])) {
    return <></>;
  }

  const isKickstarter = isKickstarterAccount(
    userData?.user.accountType ?? null,
  );

  const handleOpenUnlockModal = () => {
    setIsUnlockModalOpen(true);
  };

  return (
    <div className="p-4 lg:p-xl">
      <UnlockModal
        isOpen={isUnlockModalOpen}
        onOpenChange={setIsUnlockModalOpen}
        onUpgrade={() => {
          setIsUnlockModalOpen(false);
          navigate({ to: "/pricing" });
        }}
      />
      <UsersKickstarterBanner />
      <FeaturePageLayout
        createButton={{
          ...(isKickstarter
            ? { onClick: handleOpenUnlockModal }
            : { href: "/users/create" }),
          label: "New user",
          requiredRoles: [
            USER_ROLES.SUPER_ADMIN,
            USER_ROLES.OWNER,
            USER_ROLES.ADMIN,
            USER_ROLES.CUSTOMER_SUCCESS,
          ] as UserRole[],
        }}
        isLoading={isFetching}
        searchBar={{
          inputValue,
          setInputValue,
        }}
        tabs={
          showTabs
            ? {
                onSelectionChange: (key) => {
                  setSortDescriptor({
                    column: "createdAt",
                    direction: "descending",
                  });
                  setCategory(key as UserCategory);
                  setPage(1);
                },
                selectedKey: category,
                tabs: availableTabs,
              }
            : undefined
        }
        title="Users"
      >
        <UsersTable
          category={category}
          isLoading={isFetching}
          pagination={
            metadata
              ? {
                  metadata: {
                    currentPage: metadata.currentPage,
                    itemsPerPage: metadata.itemsPerPage,
                    totalItems: metadata.totalItems,
                    totalPages: metadata.totalPages,
                  },
                  onItemsPerPageChange: (newItemsPerPage) => {
                    setItemsPerPage(newItemsPerPage);
                    setPage(1);
                  },
                  onPageChange: setPage,
                  pageSizeOptions: getPageSizeOptions(metadata.totalItems),
                  showPageSizeSelector: true,
                }
              : undefined
          }
          setSortDescriptor={setSortDescriptor}
          sortDescriptor={sortDescriptor}
          users={users}
        />
      </FeaturePageLayout>
    </div>
  );
}
